import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useQuery, usePreventWindowUnload } from '@panel/commons/hooks';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers';
import { useGoogleMapsBootstrap } from '@panel/commons';
import { useTranslation } from 'react-i18next';
import { step1Module2Schema } from './validations';
import { locationStepSelectors, locationStepOperations } from '../../../ducks/location';
import { SavePostingOperations } from '../../../ducks/savePosting';
import { StepperOperations, StepperSelectors } from '../../../ducks/stepper';
import { LocationSection } from './LocationSection';
import { MapSection } from './MapSection';
import { formatCep, selectsConfigByPortal } from './utils';
import WrapperContainer from '../../Wrappers/WrapperContainer';
import { locationStepBody } from '../../../utils/bodyManager';
import useFormIsDirty from '../../../hooks/useFormIsDirty';
import { getStepMainSelectors, StepMainOperations } from '../../../ducks/propertyType';
import { hasCep } from '../../../utils/locationUtils';
import { site } from '@panel/commons/helpers';
import { isPortalWithPropertySubType } from '../../../utils/sitesValidations';

const siteId = site.id;
const StepLocation = () => {
  const {
    hasLocation,
    hasValues,
    isCompleted,
    getFormValuesError,
    formValues,
    isEdited,
    cep,
    visibility,
    loadingDistrict,
    zonaDemandIds,
    getLocationLoading,
    hasDataSavedOnDB
  } = useSelector(locationStepSelectors.getLocationStepState);
  const [isEdition] = useQuery(useLocation(), ['isEdition']);
  const { formValues: stepMainForm, propertySubTypeOptions } = useSelector(getStepMainSelectors);
  const { operationType, propertyType } = stepMainForm;
  const [isOnCEPView, setIsOnCEPV] = useState(true);
  const { snackAction } = useSelector(StepperSelectors.getStepperState);
  const [postingId] = useQuery(useLocation(), ['postingId']);
  // con este estado determinamos si se hizo al menos una busqueda con cep
  const [hasSearchWithCep, setHasSearchWithCep] = useState(false);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { setFormIsDirty } = useFormIsDirty();
  const { selectedCity, selectedDistrict, selectedSubDistrict } = formValues;
  const { zoneConfig } = selectsConfigByPortal[siteId] || selectsConfigByPortal.DEFAULT;
  const [lastCepSearched, setLastCepSearched] = useState(null);

  // Usar un ref para mantener el valor actualizado de hasSearchWithCep
  const hasSearchWithCepRef = useRef(hasSearchWithCep);

  useEffect(() => {
    hasSearchWithCepRef.current = hasSearchWithCep;
  }, [hasSearchWithCep]);

  const {
    register,
    handleSubmit,
    reset,
    errors,
    setValue,
    getValues,
    clearErrors,
    formState,
    trigger,
    watch
  } = useForm({
    defaultValues: {
      formValues
    },
    mode: 'onSubmit',
    shouldFocusError: true,
    resolver: yupResolver(step1Module2Schema),
    context: {
      validateDistrict: zoneConfig.mandatory && formValues?.districtData?.length > 0,
      validateCep: site.isBrasil && isOnCEPView,
      get hasSearchWithCep() {
        return hasSearchWithCepRef.current;
      }
    }
  });
  const formRef = useRef(null);
  const { isLoaded, loadError } = useGoogleMapsBootstrap({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAP_KEY
  });

  const {
    addressVal,
    cep: cepError,
    addressComplement,
    provinceVal,
    cityVal,
    districtIsRequired,
    districtVal,
    subDistrictVal
  } = errors;

  const selectedLocations = [
    selectedCity?.location_id,
    selectedDistrict?.location_id,
    selectedSubDistrict?.location_id
  ];

  const showTips =
    operationType.includes('1') &&
    selectedLocations.some(location => zonaDemandIds.includes(location)) &&
    !getLocationLoading
      ? [t('professionalPubli.tip.location')]
      : [];

  useEffect(() => {
    const formattedCep = formatCep(cep);
    if (formattedCep || formattedCep === '') {
      reset({ cep: formattedCep });
    }
  }, [cep, reset]);

  // permite la apertura del modal para prevenir perdida de datos no guardados
  useEffect(() => {
    setFormIsDirty(isEdited || formState.isDirty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdited, formState]);

  useEffect(() => {
    if (hasCep) {
      register('cep');
    }
    return () => dispatch(locationStepOperations.resetInfo());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (postingId && !hasLocation && !hasValues && !getFormValuesError) {
      const payload = {
        step: 'STEP_LOCATION',
        postingId
      };
      dispatch(locationStepOperations.getFormValues(payload));
    }
  }, [dispatch, hasLocation, hasValues, postingId, getFormValuesError]);

  useEffect(() => {
    if (hasDataSavedOnDB && isCompleted && !getFormValuesError) {
      setHasSearchWithCep(true);
      setLastCepSearched(cep);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasDataSavedOnDB, isCompleted, getFormValuesError]);

  useEffect(() => {
    if (isCompleted && !hasValues && !getFormValuesError) {
      dispatch(locationStepOperations.setEditValues(hasLocation));
    }
  }, [dispatch, hasLocation, hasValues, isCompleted, getFormValuesError]);

  useEffect(() => {
    // Traemos los subtipos de propiedad si es que existen para saber si habilitamos el boton de volver
    if (propertyType && isPortalWithPropertySubType) {
      dispatch(StepMainOperations.getSubPropertyType(propertyType));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyType]);

  const onSubmit = data => {
    dispatch(locationStepOperations.setIsEdited(true));
    // me aseguro que la visibility no se guarde en valor NONE
    const safeVisibility = visibility === 'NONE' ? 'EXACT' : visibility;
    const payload = {
      body: {
        ...locationStepBody({
          ...data,
          visibility: safeVisibility,
          postalCode: site.isBrasil ? data.cep : data.postalCode
        }),
        postingId
      },
      step: 'STEP_LOCATION'
    };
    dispatch(SavePostingOperations.savePostingDB(payload));
  };

  usePreventWindowUnload(formState.isDirty);

  // Snackbar de error para validaciones:
  const showErrorSnackbar = (actionKey, message) => {
    dispatch(
      StepperOperations.setSnackAction({
        action: actionKey,
        result: 'ERROR',
        component: message,
        section: 'Publicador profesionales - Ubicaciones',
        preventDuplicateActions: true,
        preventStacking: true
      })
    );
  };

  useEffect(() => {
    if (errors?.cep) {
      if (errors.cep.type === 'cep-validation') {
        return showErrorSnackbar('cepValidationEmpty', t(errors.cep.message));
      }
      return showErrorSnackbar('cepValidationEmpty', t('professionalPubli.location.cep.snackbar'));
    }
    if (addressVal || provinceVal || cityVal || districtIsRequired || districtVal || subDistrictVal) {
      showErrorSnackbar('agregarActionEmpty', t('professionalPubli.location.snackbar.error.generic'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors]);

  useEffect(() => {
    return () => {
      // Saneamiento del state que dispara el Snackbar:
      dispatch(StepperOperations.setSnackAction({ action: '', result: '' }));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snackAction.result]);

  const getIsValidForm = async () => {
    const res = await trigger();
    return res;
  };

  return (
    <WrapperContainer
      id='location-step'
      // Props para el title y note del Step:
      title={t('professionalPubli.location.title')}
      note={t('professionalPubli.location.note')}
      onSubmit={handleSubmit(onSubmit)}
      btnSubmitText={t('professionalPubli.cta.continue')}
      formValuesLoading={getLocationLoading || loadingDistrict}
      loadError={getFormValuesError}
      resetInfo={() => dispatch(locationStepOperations.resetInfo())}
      getIsValidForm={getIsValidForm}
      tips={showTips}
      // Evitamos la navegacion al paso 1 una vez cargado cuando es creacion, a menos que el portal tenga subtipos de propiedad
      enableBackButton={isEdition || (isPortalWithPropertySubType && propertySubTypeOptions?.length > 0)}
    >
      <form data-publi='locationStep-form' id='locationStep' ref={formRef} onSubmit={handleSubmit(onSubmit)}>
        <fieldset disabled={getLocationLoading} data-publi='locationStep-form-fieldset'>
          <LocationSection
            register={register}
            errors={errors}
            trigger={trigger}
            isLoaded={isLoaded}
            reset={reset}
            loadError={loadError}
            clearErrors={clearErrors}
            setValue={setValue}
            getValues={getValues}
            watch={watch}
            isOnCEPView={isOnCEPView}
            setIsOnCEPV={setIsOnCEPV}
            setHasSearchWithCep={setHasSearchWithCep}
            lastCepSearched={lastCepSearched}
            setLastCepSearched={setLastCepSearched}
            isEdition={isEdition}
            hasDataSavedOnDB={hasDataSavedOnDB}
            showErrorSnackbar={showErrorSnackbar}
            hasSearchWithCep={hasSearchWithCep}
          />
          <MapSection isLoaded={isLoaded} loadError={loadError} />
        </fieldset>
      </form>
    </WrapperContainer>
  );
};

export default StepLocation;
